import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TrendingUp, 
  Users, 
  Calendar, 
  FileText, 
  DollarSign,
  Clock,
  CheckCircle,
  AlertCircle,
  Star,
  Building
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '../../ui/avatar';

const OrganizationDashboard = ({ orgData }) => {
  const navigate = useNavigate();
  const recentRequirements = [
    {
      id: 1,
      title: 'Leadership Training for Middle Management',
      status: 'active',
      proposals: 12,
      postedDate: '2024-09-25',
      budget: '$15,000 - $20,000'
    },
    {
      id: 2,
      title: 'Technical Skills Workshop',
      status: 'completed',
      proposals: 8,
      postedDate: '2024-09-20',
      budget: '$8,000 - $12,000'
    },
    {
      id: 3,
      title: 'Team Building Program',
      status: 'pending',
      proposals: 5,
      postedDate: '2024-09-28',
      budget: '$5,000 - $8,000'
    }
  ];

  const upcomingTrainings = [
    {
      id: 1,
      title: 'Executive Leadership Workshop',
      trainer: 'Dr. Sarah Johnson',
      date: '2024-10-02',
      time: '09:00 AM - 5:00 PM',
      participants: 25,
      venue: 'Conference Room A'
    },
    {
      id: 2,
      title: 'Digital Transformation Training',
      trainer: 'Michael Chen',
      date: '2024-10-05',
      time: '10:00 AM - 4:00 PM',
      participants: 40,
      venue: 'Virtual Session'
    },
    {
      id: 3,
      title: 'Project Management Fundamentals',
      trainer: 'Emily Rodriguez',
      date: '2024-10-08',
      time: '9:30 AM - 3:30 PM',
      participants: 15,
      venue: 'Training Center'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckCircle className="w-4 h-4" />;
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'completed': return <Star className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Key Metrics - Clickable Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card 
          className="bg-gradient-to-r from-blue-500 to-blue-600 text-white cursor-pointer hover:shadow-xl transition-all duration-200 hover:scale-105"
          onClick={() => navigate('/business/purchase-orders')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Total Investment</p>
                <p className="text-2xl font-bold">${orgData.totalSpent.toLocaleString()}</p>
                <p className="text-blue-200 text-xs">+12% from last month</p>
                <p className="text-blue-100 text-xs mt-1">Click to view details</p>
              </div>
              <DollarSign className="w-8 h-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-gradient-to-r from-green-500 to-green-600 text-white cursor-pointer hover:shadow-xl transition-all duration-200 hover:scale-105"
          onClick={() => navigate('/business/my-trainers')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Active Trainers</p>
                <p className="text-2xl font-bold">{orgData.activeTrainers}</p>
                <p className="text-green-200 text-xs">2 new this month</p>
                <p className="text-green-100 text-xs mt-1">Click to view trainers</p>
              </div>
              <Users className="w-8 h-8 text-green-200" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-gradient-to-r from-purple-500 to-purple-600 text-white cursor-pointer hover:shadow-xl transition-all duration-200 hover:scale-105"
          onClick={() => navigate('/business/training-schedule')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Upcoming Trainings</p>
                <p className="text-2xl font-bold">{orgData.upcomingTrainings}</p>
                <p className="text-purple-200 text-xs">Next 30 days</p>
                <p className="text-purple-100 text-xs mt-1">Click to view schedule</p>
              </div>
              <Calendar className="w-8 h-8 text-purple-200" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-gradient-to-r from-orange-500 to-orange-600 text-white cursor-pointer hover:shadow-xl transition-all duration-200 hover:scale-105"
          onClick={() => navigate('/business/proposals-received')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm">New Proposals</p>
                <p className="text-2xl font-bold">{orgData.notifications.proposals}</p>
                <p className="text-orange-200 text-xs">Awaiting review</p>
                <p className="text-orange-100 text-xs mt-1">Click to review</p>
              </div>
              <FileText className="w-8 h-8 text-orange-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Training Requirements */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center">
                <FileText className="w-5 h-5 mr-2 text-blue-600" />
                Recent Requirements
              </span>
              <Button size="sm" variant="outline">View All</Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentRequirements.map((req) => (
                <div key={req.id} className="border rounded-lg p-4 hover:shadow-sm transition-shadow">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-gray-900 text-sm">{req.title}</h4>
                    <Badge className={`${getStatusColor(req.status)} flex items-center space-x-1`}>
                      {getStatusIcon(req.status)}
                      <span className="capitalize">{req.status}</span>
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <div className="flex items-center space-x-4">
                      <span>{req.proposals} proposals</span>
                      <span>•</span>
                      <span>{req.budget}</span>
                    </div>
                    <span>{new Date(req.postedDate).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Upcoming Training Sessions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-green-600" />
                Upcoming Sessions
              </span>
              <Button size="sm" variant="outline">View Calendar</Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {upcomingTrainings.map((training) => (
                <div key={training.id} className="border rounded-lg p-4 hover:shadow-sm transition-shadow">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-gray-900 text-sm">{training.title}</h4>
                    <Badge variant="outline" className="text-xs">
                      {training.participants} attendees
                    </Badge>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-600 mb-2">
                    <Users className="w-4 h-4" />
                    <span className="font-medium">{training.trainer}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <div className="flex items-center space-x-4">
                      <span>{new Date(training.date).toLocaleDateString()}</span>
                      <span>•</span>
                      <span>{training.time}</span>
                    </div>
                    <span className="text-blue-600">{training.venue}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action Items */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <AlertCircle className="w-5 h-5 mr-2 text-orange-600" />
            Action Items
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-red-800">Pending Proposals</h4>
                <Badge variant="destructive">{orgData.notifications.proposals}</Badge>
              </div>
              <p className="text-red-700 text-sm mb-3">Review and respond to training proposals</p>
              <Button size="sm" className="bg-red-600 hover:bg-red-700">
                Review Proposals
              </Button>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-yellow-800">Upcoming Payments</h4>
                <Badge className="bg-yellow-100 text-yellow-800">{orgData.notifications.orders}</Badge>
              </div>
              <p className="text-yellow-700 text-sm mb-3">Process pending trainer payments</p>
              <Button size="sm" className="bg-yellow-600 hover:bg-yellow-700">
                View Orders
              </Button>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-green-800">New Messages</h4>
                <Badge className="bg-green-100 text-green-800">{orgData.notifications.messages}</Badge>
              </div>
              <p className="text-green-700 text-sm mb-3">Respond to trainer communications</p>
              <Button size="sm" className="bg-green-600 hover:bg-green-700">
                Read Messages
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OrganizationDashboard;